import {
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Box,
} from '@mui/material'
import {useState, useMemo} from 'react'
import useYoutubeUploadStatus from 'logic/Dances/useYoutubeUploadStatus'
import {useI18n} from 'store/Store'
import ReplayIcon from '@mui/icons-material/Replay'
import TableCellRenderer from './TableCellRenderer'

const StatusesTable = ({videoType}) => {
    const {i18n} = useI18n()
    const [statusFilter, setStatusFilter] = useState('all')

    const {headers, data, iconsMap, retryUpload, isLoading, isRetrying} =
        useYoutubeUploadStatus(videoType)

    const availableStatuses = useMemo(() => {
        if (!data?.items) return []
        const statuses = new Set(data.items.map((item) => item.youtubeStatus))
        return Array.from(statuses).filter(Boolean).sort()
    }, [data?.items])

    const filteredItems = useMemo(() => {
        if (!data?.items) return []
        if (statusFilter === 'all') return data.items
        return data.items.filter((item) => item.youtubeStatus === statusFilter)
    }, [data?.items, statusFilter])

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}>
                <Typography fontWeight={500}>
                    {i18n.t('dances.youtubeUploadStatusTableTitle', {
                        count: filteredItems.length,
                    })}
                </Typography>

                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2">
                        {i18n.t('dances.filterByStatus')}:
                    </Typography>
                    <FormControl size="small" sx={{minWidth: 200}}>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="all">
                                {i18n.t('dances.allStatuses')}
                            </MenuItem>
                            {availableStatuses.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {i18n.t(`dances.youtubeStatus.${status}`, {
                                        defaultValue: status,
                                    })}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <TableContainer variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {headers.map((header) => (
                                <TableCell key={header}>
                                    {i18n.t(`dances.${header}`)}
                                </TableCell>
                            ))}
                            <TableCell align="center" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={headers.length + 1}
                                    align="center">
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredItems.map((item) => (
                                <TableRow key={item._id}>
                                    {headers.map((header) => (
                                        <TableCellRenderer
                                            key={header}
                                            header={header}
                                            item={item}
                                            iconsMap={iconsMap}
                                        />
                                    ))}
                                    <TableCell align="center">
                                        {item.youtubeStatus === 'failed' && (
                                            <Tooltip title="Retry upload">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    disabled={isRetrying}
                                                    onClick={() => {
                                                        retryUpload(item._id)
                                                    }}>
                                                    {isRetrying ? (
                                                        <CircularProgress
                                                            size={16}
                                                        />
                                                    ) : (
                                                        <ReplayIcon />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default StatusesTable
